import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormData } from "../AdvisorWizard";
import { User, Heart, PiggyBank, Target } from "lucide-react";

interface PersonalStepProps {
  data: FormData;
  onComplete: (data: Partial<FormData>) => void;
}

const PersonalStep = ({ data, onComplete }: PersonalStepProps) => {
  const [formData, setFormData] = useState<Partial<FormData>>({
    age: data.age || '',
    familyStatus: data.familyStatus || '',
    financialSituation: data.financialSituation || '',
    mainReason: data.mainReason || ''
  });

  const ageOptions = [
    { value: '60-65', label: '60-65 Jahre', subtitle: 'Jüngere Senioren' },
    { value: '66-70', label: '66-70 Jahre', subtitle: 'Frühe Rente' },
    { value: '71-75', label: '71-75 Jahre', subtitle: 'Standard-Zielgruppe' },
    { value: '76-80', label: '76-80 Jahre', subtitle: 'Optimales Alter für Leibrente' },
    { value: '81+', label: '81+ Jahre', subtitle: 'Hochbetagte' }
  ];

  const familyStatusOptions = [
    { value: 'Single', label: 'Alleinstehend', icon: User },
    { value: 'Verheiratet', label: 'Verheiratet', icon: Heart },
    { value: 'Verwitwet', label: 'Verwitwet', icon: User },
    { value: 'Geschieden', label: 'Geschieden', icon: User }
  ];

  const financialOptions = [
    { value: 'Sehr gut', label: 'Sehr gut', subtitle: 'Keine finanziellen Sorgen' },
    { value: 'Gut', label: 'Gut', subtitle: 'Stabile Verhältnisse' },
    { value: 'Ausreichend', label: 'Ausreichend', subtitle: 'Grundbedürfnisse gedeckt' },
    { value: 'Schwierig', label: 'Schwierig', subtitle: 'Finanzielle Engpässe' }
  ];

  const reasonOptions = [
    { value: 'Rente aufstocken', label: 'Rente aufstocken', subtitle: 'Regelmäßige Zusatzeinkünfte' },
    { value: 'Pflegekosten finanzieren', label: 'Pflegekosten finanzieren', subtitle: 'Zukünftige Pflegebedürftigkeit' },
    { value: 'Familie unterstützen', label: 'Familie unterstützen', subtitle: 'Kinder/Enkel unterstützen' },
    { value: 'Renovierung finanzieren', label: 'Renovierung finanzieren', subtitle: 'Immobilie modernisieren' },
    { value: 'Mehr Lebensqualität', label: 'Mehr Lebensqualität', subtitle: 'Reisen, Hobbys' },
    { value: 'Sonstiges', label: 'Sonstiges', subtitle: 'Andere Gründe' }
  ];

  const handleOptionSelect = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isComplete = () => {
    return formData.age && formData.familyStatus && formData.financialSituation && formData.mainReason;
  };

  const handleNext = () => {
    if (isComplete()) {
      onComplete(formData);
    }
  };

  return (
    <Card className="card-elevated">
      <CardHeader className="text-center pb-6">
        <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
          <User className="h-8 w-8 text-accent" />
        </div>
        <CardTitle className="text-2xl md:text-3xl text-primary">
          Schritt 1: Ihre persönliche Situation
        </CardTitle>
        <p className="text-lg text-muted-foreground">
          Erzählen Sie uns ein wenig über sich, damit wir Ihnen passende Optionen vorschlagen können.
        </p>
      </CardHeader>
      
      <CardContent className="space-y-8">
        {/* Alter */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
            Wie alt sind Sie?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ageOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('age', option.value)}
                className={`p-3 sm:p-4 text-left border rounded-lg transition-all hover:shadow-md min-h-[70px] flex flex-col justify-center ${
                  formData.age === option.value
                    ? 'border-accent bg-accent/5 shadow-md'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-tight break-words">{option.subtitle}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Familienstand */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
            Ihr Familienstand
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {familyStatusOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect('familyStatus', option.value)}
                  className={`p-3 sm:p-4 text-center border rounded-lg transition-all hover:shadow-md min-h-[80px] flex flex-col items-center justify-center ${
                    formData.familyStatus === option.value
                      ? 'border-accent bg-accent/5 shadow-md'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-accent flex-shrink-0" />
                  <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words w-full">{option.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Finanzielle Situation */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
            Ihre finanzielle Situation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {financialOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('financialSituation', option.value)}
                className={`p-3 sm:p-4 text-left border rounded-lg transition-all hover:shadow-md min-h-[70px] flex flex-col justify-center ${
                  formData.financialSituation === option.value
                    ? 'border-accent bg-accent/5 shadow-md'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-tight break-words">{option.subtitle}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Hauptgrund */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
            Hauptgrund für die Verrentung
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reasonOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('mainReason', option.value)}
                className={`p-3 sm:p-4 text-left border rounded-lg transition-all hover:shadow-md min-h-[70px] flex flex-col justify-center ${
                  formData.mainReason === option.value
                    ? 'border-accent bg-accent/5 shadow-md'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-tight break-words">{option.subtitle}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Weiter Button */}
        <div className="pt-6 flex justify-center">
          <Button 
            variant="accent" 
            size="lg" 
            onClick={handleNext}
            disabled={!isComplete()}
            className="text-base sm:text-lg md:text-xl px-6 py-3 sm:px-8 md:px-12 h-auto leading-tight text-center whitespace-normal max-w-xs sm:max-w-sm md:max-w-none"
          >
            <span className="block">
              Weiter zu Schritt 2:<br className="sm:hidden" />
              <span className="sm:ml-1">Ihre Immobilie</span>
            </span>
          </Button>
          {!isComplete() && (
            <p className="text-sm text-muted-foreground mt-2">
              Bitte beantworten Sie alle Fragen, um fortzufahren
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalStep;